import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { DailyPerformanceRecord } from '../../types/styling-analysis.js';
import type { Granularity } from '../../lib/kpi-aggregation.js';
import { GRANULARITY_OPTIONS, isWeekend, periodKey } from '../../lib/kpi-aggregation.js';
import {
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_CLICKABLE_PROPS,
} from '../../lib/chart-theme.js';
import { useSeriesToggle } from '../../hooks/useSeriesToggle.js';
import { ChartFilterBar } from '../chart/ChartFilterBar.js';

/** @graph-connects none */
export type DayType = 'all' | 'weekday' | 'weekend';

/** @graph-connects none */
const DAY_TYPE_OPTION_KEYS = [
  { key: 'all' as const, labelKey: 'styling.dayTypeAll' },
  { key: 'weekday' as const, labelKey: 'styling.dayTypeWeekday' },
  { key: 'weekend' as const, labelKey: 'styling.dayTypeWeekend' },
] as const;

/** @graph-connects none */
const CHART_COLORS = {
  bar: '#0017C1',
  time: '#FE3939',
  stylist: '#7096F8',
} as const;

/** @graph-connects none */
interface ChartDataPoint {
  label: string;
  completedCount: number;
  avgMinutes: number;
  activeStylistCount: number;
}

/** @graph-connects none */
export function aggregatePerformance(
  records: DailyPerformanceRecord[],
  granularity: Granularity,
  dayType: DayType,
): ChartDataPoint[] {
  let filtered = records;
  if (dayType === 'weekday') filtered = records.filter((r) => !isWeekend(r.date));
  else if (dayType === 'weekend') filtered = records.filter((r) => isWeekend(r.date));

  if (granularity === 'daily') {
    return filtered.map((r) => ({
      label: r.date.slice(5),
      completedCount: r.completedCount,
      avgMinutes: r.avgMinutes,
      activeStylistCount: r.activeStylistCount,
    }));
  }

  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? -1 : 1));
  const buckets = new Map<string, DailyPerformanceRecord[]>();
  for (const row of sorted) {
    const key = periodKey(row.date, granularity);
    const arr = buckets.get(key);
    if (arr) {
      arr.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }

  const result: ChartDataPoint[] = [];
  for (const [key, rows] of buckets) {
    const totalCount = rows.reduce((s, r) => s + r.completedCount, 0);
    const avgMinutes =
      totalCount > 0
        ? Math.round(
            (rows.reduce((s, r) => s + r.avgMinutes * r.completedCount, 0) / totalCount) * 10,
          ) / 10
        : 0;
    const avgStylists =
      Math.round((rows.reduce((s, r) => s + r.activeStylistCount, 0) / rows.length) * 10) / 10;
    result.push({
      label: key,
      completedCount: totalCount,
      avgMinutes,
      activeStylistCount: avgStylists,
    });
  }

  return result;
}

interface StylingPerformanceChartProps {
  records: DailyPerformanceRecord[];
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定パフォーマンス推移チャート。棒グラフ（件数）+折れ線（平均時間・稼働人数）を粒度・曜日フィルタ付きで表示。
 * @graph-connects aggregatePerformance [calls] 日別データを粒度・曜日でフィルタ集約
 */
export function StylingPerformanceChart({ records }: StylingPerformanceChartProps) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [dayType, setDayType] = useState<DayType>('all');
  const { hiddenSeries, handleLegendClick } = useSeriesToggle();

  const data = useMemo(
    () => aggregatePerformance(records, granularity, dayType),
    [records, granularity, dayType],
  );

  const dayTypeOptions = useMemo(
    () => DAY_TYPE_OPTION_KEYS.map(({ key, labelKey }) => ({ key, label: t(labelKey) })),
    [t],
  );

  const summary = useMemo(() => {
    if (data.length === 0)
      return { totalCount: 0, avgCount: 0, avgTime: 0, minTime: 0, maxTime: 0, avgStylists: 0 };
    const totalCount = data.reduce((s, d) => s + d.completedCount, 0);
    const times = data.map((d) => d.avgMinutes);
    return {
      totalCount,
      avgCount: Math.round((totalCount / data.length) * 10) / 10,
      avgTime:
        totalCount > 0
          ? Math.round(
              (data.reduce((s, d) => s + d.avgMinutes * d.completedCount, 0) / totalCount) * 10,
            ) / 10
          : 0,
      minTime: Math.min(...times),
      maxTime: Math.max(...times),
      avgStylists:
        Math.round((data.reduce((s, d) => s + d.activeStylistCount, 0) / data.length) * 10) / 10,
    };
  }, [data]);

  const granLabel = granularity === 'daily' ? t('styling.perDay') : t('styling.perPeriod');

  if (data.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('styling.noDataPeriod')}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <ChartFilterBar
          label={t('styling.granularity')}
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={setGranularity}
        />
        <ChartFilterBar
          label={t('styling.dayType')}
          options={dayTypeOptions}
          value={dayType}
          onChange={setDayType}
        />
      </div>

      <Card className="p-4">
        <div style={{ width: '100%', height: 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                yAxisId="left"
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                unit={t('styling.minUnit')}
              />
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                formatter={
                  ((value: number, name: string) => [value.toLocaleString('ja-JP'), name]) as never
                }
              />
              <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
              <Bar
                yAxisId="left"
                dataKey="completedCount"
                name={t('styling.selectionCount')}
                fill={CHART_COLORS.bar}
                opacity={0.75}
                radius={[2, 2, 0, 0]}
                hide={hiddenSeries.has('completedCount')}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgMinutes"
                name={t('styling.avgSelectionTimeMin')}
                stroke={CHART_COLORS.time}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                hide={hiddenSeries.has('avgMinutes')}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="activeStylistCount"
                name={t('styling.activeStylistCount')}
                stroke={CHART_COLORS.stylist}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                hide={hiddenSeries.has('activeStylistCount')}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">{t('styling.periodTotal')}</div>
          <div className="mt-1 text-xl font-bold tabular-nums">
            {summary.totalCount.toLocaleString('ja-JP')}
            <span className="ml-0.5 text-sm font-normal text-[var(--color-text-secondary)]">
              {t('styling.countUnit')}
            </span>
          </div>
          <div className="mt-0.5 text-xs tabular-nums text-[var(--color-text-muted)]">
            {t('styling.avgCountPerUnit', { value: String(summary.avgCount), unit: granLabel })}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">
            {t('styling.avgSelectionTime')}
          </div>
          <div className="mt-1 text-xl font-bold tabular-nums">
            {summary.avgTime}
            <span className="ml-0.5 text-sm font-normal text-[var(--color-text-secondary)]">
              {t('styling.minUnit')}
            </span>
          </div>
          <div className="mt-0.5 text-xs tabular-nums text-[var(--color-text-muted)]">
            {t('styling.minMaxTime', {
              min: String(summary.minTime),
              max: String(summary.maxTime),
            })}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">
            {t('styling.activeStylists')}
          </div>
          <div className="mt-1 text-xl font-bold tabular-nums">
            {summary.avgStylists}
            <span className="ml-0.5 text-sm font-normal text-[var(--color-text-secondary)]">
              {t('styling.peoplePerUnit', { unit: granLabel })}
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
